import { useMemo } from 'react';
import { FilterDropdown } from './FilterDropdown';

// Phase 65 D-07–D-09: Month-only selector for invoice creation.
// Wraps the project-standard <FilterDropdown> (the "фирменная" dropdown style
// used by manager filters in BloggersView) — does NOT re-implement the menu.
//
// Renders 13 options: current calendar month ±6 months. Labels are RU long-form
// ("апрель 2026") via Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).
// Some Node/V8 builds emit a trailing " г." suffix — stripped via regex so the
// label matches the spec exactly. Falls back to a hardcoded 12-month name array
// if the runtime returns an unexpected shape.

const RU_MONTHS_NOMINATIVE = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

function formatMonthYearRu(d) {
  try {
    const raw = d.toLocaleDateString('ru-RU', {
      month: 'long', year: 'numeric', timeZone: 'UTC',
    });
    // Strip trailing " г." or " г" if present (older V8 / ICU variants).
    const cleaned = raw.replace(/\s*г\.?$/i, '').trim();
    if (cleaned && /[а-яa-z]/i.test(cleaned)) return cleaned;
  } catch {

  }
  return `${RU_MONTHS_NOMINATIVE[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Option list of <MonthPicker> (pure; MF-09 range test). Default: now−6 … now+6.
 * `from` ('YYYY-MM') moves the first month back (never forward); the last stays now+6.
 * `emptyLabel` prepends a '' option so a filter can be cleared.
 */
export function monthOptions({ from, emptyLabel } = {}, now = new Date()) {
  const baseY = now.getUTCFullYear();
  const baseM = now.getUTCMonth();
  const [fromY, fromM] = from ? from.split('-').map(Number) : [];
  const back = from ? Math.max(6, (baseY - fromY) * 12 + baseM - (fromM - 1)) : 6;
  const months = Array.from({ length: back + 7 }, (_, i) => {
    const d = new Date(Date.UTC(baseY, baseM - back + i, 1));
    const v = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    return { value: v, label: formatMonthYearRu(d) };
  });
  return emptyLabel ? [{ value: '', label: emptyLabel }, ...months] : months;
}

/**
 * Month-only selector for invoice creation (Phase 65 D-07–D-09) and month filters (MF-09).
 *
 * @param {{
 *   value: string|null,
 *   onChange: (next: string) => void,
 *   placeholder?: string,
 *   from?: string,
 *   emptyLabel?: string,
 * }} props - `value` is a 'YYYY-MM' string. `onChange` receives a new 'YYYY-MM' string
 *   ('' for the `emptyLabel` option).
 */
export function MonthPicker({ value, onChange, placeholder = 'Месяц', from, emptyLabel }) {
  const options = useMemo(() => monthOptions({ from, emptyLabel }), [from, emptyLabel]);

  return (
    <FilterDropdown
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
    />
  );
}

export default MonthPicker;
